import { SITE_URL } from './site';
import type { NewsletterAudience } from '../api/queries';

/**
 * Starting points for a letter. Each is the layout of a kind of letter the lab
 * sends, with `[square-bracket]` prompts where its own words go — the API
 * refuses to send while one is left in (`unfilledPrompt`), so a prompt can
 * never reach a reader. Nothing here is news: the prompts say what to write.
 *
 * The formatting is the composer's (and the API's, domain/newsletter.ts): a
 * blank line between paragraphs, `- ` for a list, `## ` for a section,
 * `[words](link)` for a link, a line of only `[Label](link)` for a button,
 * `**words**` for bold, `{first name}` for the reader's first name.
 *
 * Plain language, as on the public site: no "enroll", "participate",
 * "eligibility" or "treatment" — "join a study", "whether a study is a fit".
 */
export interface LetterTemplate {
  key: string;
  name: string;
  /** One line on when to use it. */
  use: string;
  /** The list it is usually for, shown as a hint; choosing it changes nothing. */
  suits?: NewsletterAudience;
  subject: string;
  body: string;
}

export const LETTER_TEMPLATES: LetterTemplate[] = [
  {
    key: 'lab-letter',
    name: 'A letter from the lab',
    use: 'The regular update: what is new, what is open, what has published.',
    suits: 'letter',
    subject: 'A letter from the lab — [month]',
    body: `Hello {first name},

[One or two sentences on what has happened at the lab since the last letter.]

## Studies open now
- **[Study name]** — [one line on who it is for]: [read about it](${SITE_URL}/research)

## What has published
- **[Paper title]** — [one line on what it found]: [read the paper](https://doi.org/[DOI])

## What comes next
[A sentence or two on what the lab is working toward.]

[Explore the research](${SITE_URL}/research)

With thanks,
The ENACT team`,
  },
  {
    key: 'study-open',
    name: 'A study is now open',
    use: 'Tell the people following a study that it is taking inquiries.',
    suits: 'studies',
    subject: '[Study name] is now open',
    body: `Hello {first name},

You asked to hear when [study name] opened. It is now taking inquiries.

## About the study
[Two or three plain sentences: who the study is for and what taking part involves.]

Sending an inquiry does not put you in a study. A member of the study team will be in touch to talk it through, and whether the study is a fit is decided later, not in the form.

[Send an inquiry](${SITE_URL}/join)

If you are in immediate danger, call or text 988.

The ENACT team`,
  },
  {
    key: 'publication',
    name: 'New publication',
    use: 'Share a paper from an ENACT study, in plain words.',
    suits: 'letter',
    subject: 'New from ENACT: [short title]',
    body: `Hello {first name},

ENACT researchers have published [paper title] in [journal].

## What the study looked at
[Two or three plain sentences.]

## What it found
[Two or three plain sentences. Keep to what the paper says.]

[Read the paper](https://doi.org/[DOI])

The ENACT team`,
  },
  {
    key: 'event',
    name: 'Event or talk',
    use: 'Invite readers to a talk, webinar or open day.',
    suits: 'letter',
    subject: '[Event name] — [date]',
    body: `Hello {first name},

[Name] from ENACT will speak at [event] on [date].

## Details
- **When:** [date and time]
- **Where:** [place, or the link to join]
- **Who it is for:** [who should come]

[Find out more]([link])

The ENACT team`,
  },
  {
    key: 'blank',
    name: 'Blank letter',
    use: 'Start from a greeting and a sign-off.',
    subject: '',
    body: `Hello {first name},



The ENACT team`,
  },
];
